import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { 
  Box, 
  Typography, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Grid,
  Chip,
  CircularProgress,
  useTheme,
  Divider,
  useMediaQuery,
  IconButton
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import enLocale from 'date-fns/locale/en-GB';
import { fetchHistoricalTradesList } from '../../services/backendAPI';
import AssessmentIcon from '@mui/icons-material/Assessment';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useSnackbar } from 'notistack';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { fetchTradeHistory } from '../../store/actions/orders';
import { formatTimeToIST } from '../../utils/dateUtils';
import ExportTradeHistoryModal from './ExportTradeHistoryModal';

const TradeHistoryDetails = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  
  // Function to check if a date is a weekend (Saturday or Sunday)
  const isWeekend = (date) => {
    const day = date.getDay();
    return day === 0 || day === 6; // 0 = Sunday, 6 = Saturday
  };
  
  // Function to get the most recent business day
  const getMostRecentBusinessDay = () => {
    const today = new Date();
    let recentBusinessDay = new Date(today);
    
    // If today is Monday, set to previous Friday
    if (today.getDay() === 1) { // Monday
      recentBusinessDay.setDate(today.getDate() - 3);
      return recentBusinessDay;
    }
    
    // If today is Sunday, set to previous Friday
    if (today.getDay() === 0) { // Sunday
      recentBusinessDay.setDate(today.getDate() - 2);
      return recentBusinessDay;
    }
    
    // If today is Saturday, set to previous Friday
    if (today.getDay() === 6) { // Saturday
      recentBusinessDay.setDate(today.getDate() - 1);
      return recentBusinessDay;
    }
    
    // For regular weekdays, use yesterday
    recentBusinessDay.setDate(today.getDate() - 1);
    return recentBusinessDay;
  };
  
  // Table data state
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  
  // Filter states - use most recent business day for date
  const [date, setDate] = useState(new Date());
  const [exchange, setExchange] = useState('NFO');
  const [buySell, setBuySell] = useState('');
  const [isErrorTrade, setIsErrorTrade] = useState(false);
  const dispatch = useDispatch();
  
  // Export modal state
  const [openExportModal, setOpenExportModal] = useState(false);
  
  const formatDate = (date) => {
    if (!date) return '';
    return format(date, 'yyyy-MM-dd');
  };
  
  const fetchTrades = async (newPage, newRowsPerPage) => {
    setLoading(true);
    try {
      const params = {
        page: newPage + 1,
        pageSize: newRowsPerPage,
        fetchAll: 'ALL',
        exchange: exchange,
        businessDate: formatDate(date),
        buySell: buySell === '2' ? undefined : buySell,
        isErrorTrade: isErrorTrade
      };
      
      const result = await dispatch(fetchTradeHistory(params));
      console.log("Component received result:", result);
      
      if (result && result.trades) {
        setTrades(result.trades);
        setTotalCount(result.total_count || 0);
      } else if (result && result.data && result.data.trades) {
        setTrades(result.data.trades);
        setTotalCount(result.data.total_count || 0);
      } else {
        throw new Error('Failed to fetch trade history');
      }
    } catch (error) {
      console.error("Component error:", error);
      enqueueSnackbar(error.message || 'Error fetching trade history', { 
        variant: 'error',
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
        autoHideDuration: 3000
      });
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchTrades(page, rowsPerPage);
  }, []);
  
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    fetchTrades(newPage, rowsPerPage);
  };
  
  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    fetchTrades(0, newRowsPerPage);
  };

  const fetchReset = async () => {
    setLoading(true);
    try {
      const params = {
        page: 0,
        pageSize: 50,
        fetchAll: 'ALL',
        exchange: "NFO",
        businessDate: formatDate(new Date()),
        isErrorTrade: false
      };
      
      const result = await dispatch(fetchTradeHistory(params));
      console.log("Component received result:", result);
      
      if (result && result.trades) {
        setTrades(result.trades);
        setTotalCount(result.total_count || 0);
      } else if (result && result.data && result.data.trades) {
        setTrades(result.data.trades);
        setTotalCount(result.data.total_count || 0);
      } else {
        throw new Error('Failed to fetch trade history');
      }
    } catch (error) {
      console.error("Component error:", error);
      enqueueSnackbar(error.message || 'Error fetching trade history', { 
        variant: 'error',
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
        autoHideDuration: 3000
      });
    } finally {
      setLoading(false);
    }
  };

  
  const handleApplyFilters = () => {
    setPage(0);
    fetchTrades(0, rowsPerPage);
  };
  
  const handleResetFilters = () => {
    setDate(new Date());
    setExchange('NFO');
    setBuySell('2');
    setIsErrorTrade(false);
    setPage(0);
    
    // Reset and fetch with default values
    fetchReset();
  };
  
  const handleOpenExportModal = () => {
    setOpenExportModal(true);
  };
  
  const handleCloseExportModal = () => {
    setOpenExportModal(false);
  };
  
  return (
    <Box sx={{ p: isMobile ? 1 : 2, height: '100%', overflow: 'auto' }}>
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? 2 : 0,
        mb: 3 
      }}>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center',
          width: isMobile ? '100%' : 'auto'
        }}>
          <IconButton 
            onClick={() => navigate('/trade-history')}
            sx={{ 
              mr: 1,
              color: theme.palette.primary.main,
              '&:hover': {
                backgroundColor: theme.palette.action.hover
              }
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <AssessmentIcon sx={{ 
            mr: 1, 
            color: theme.palette.primary.main,
            fontSize: isMobile ? 24 : 28
          }} />
          <Typography variant={isMobile ? "h6" : "h5"} component="h1" sx={{ 
            fontWeight: 600,
            color: theme.palette.primary.main
          }}>
            Trade History Details
          </Typography>
        </Box>
        
        <Button
          variant="contained"
          color="primary"
          startIcon={<DownloadIcon />}
          onClick={handleOpenExportModal}
          fullWidth={isMobile}
        >
          Export
        </Button>
      </Box>
      
      {/* Filters */}
      <Paper sx={{ p: isMobile ? 1.5 : 2, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Filters</Typography>
        <Grid container spacing={isMobile ? 1.5 : 2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enLocale}>
              <DatePicker
                label="Date"
                value={date}
                onChange={(newValue) => setDate(newValue)}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    fullWidth 
                    size={isMobile ? "small" : "medium"}
                    InputLabelProps={{
                      style: { fontWeight: 600 }
                    }}
                  />
                )}
                inputFormat="dd/MM/yyyy"
                maxDate={new Date()}
              />
            </LocalizationProvider>
          </Grid>
          
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size={isMobile ? "small" : "medium"}>
              <InputLabel id="exchange-select-label">Exchange</InputLabel>
              <Select
                labelId="exchange-select-label"
                value={exchange}
                label="Exchange"
                onChange={(e) => setExchange(e.target.value)}
              >
                <MenuItem value="NFO">Derivatives</MenuItem>
                <MenuItem value="MCX">Commodities</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size={isMobile ? "small" : "medium"}>
              <InputLabel id="buysell-select-label">Buy/Sell</InputLabel>
              <Select
                labelId="buysell-select-label"
                value={buySell}
                label="Buy/Sell"
                onChange={(e) => setBuySell(e.target.value)}
              >
                <MenuItem value="2">All</MenuItem>
                <MenuItem value="1">Buy</MenuItem>
                <MenuItem value="0">Sell</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size={isMobile ? "small" : "medium"}>
              <InputLabel id="error-trade-select-label">Error Trades</InputLabel>
              <Select
                labelId="error-trade-select-label"
                value={isErrorTrade}
                label="Error Trades"
                onChange={(e) => setIsErrorTrade(e.target.value)}
              >
                <MenuItem value={false}>No</MenuItem>
                <MenuItem value={true}>Yes</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ 
              display: 'flex', 
              gap: 1,
              flexDirection: isMobile ? 'column' : 'row',
              width: '100%'
            }}>
              <Button 
                variant="contained" 
                startIcon={<SearchIcon />} 
                onClick={handleApplyFilters}
                disabled={loading}
                fullWidth={isMobile}
                size={isMobile ? "small" : "medium"}
              >
                Search
              </Button>
              <Button 
                variant="outlined" 
                startIcon={<RefreshIcon />} 
                onClick={handleResetFilters}
                disabled={loading}
                fullWidth={isMobile}
                size={isMobile ? "small" : "medium"}
              >
                Reset
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>
      
      {/* Results */}
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer sx={{ 
          maxHeight: isMobile ? 'calc(100vh - 400px)' : 'calc(100vh - 300px)',
          overflow: 'auto'
        }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : trades.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
              <Typography variant="body1" color="text.secondary">
                No trade history found for the selected filters
              </Typography>
            </Box>
          ) : (
            <>
              {isMobile ? (
                // Mobile view - Card layout
                <Box sx={{ p: 1 }}>
                  {trades.map((trade) => (
                    <Paper
                      key={trade.trade_id || trade.id}
                      sx={{
                        p: 2,
                        mb: 2,
                        borderRadius: 1,
                        boxShadow: 1,
                        backgroundColor: theme.palette.background.paper
                      }}
                    >
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                            Order ID
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {trade.order_id}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                            Trade ID
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {trade.trade_id || trade.id}
                          </Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                            Symbol
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {trade.trading_symbol}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                            Trade Time
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {formatTimeToIST(trade.exchange_time)}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                            Buy/Sell
                          </Typography>
                          <Chip 
                            label={trade.buy_sell === 0 ? "BUY" : "SELL"} 
                            color={trade.buy_sell === 0 ? "success" : "error"}
                            size="small"
                            sx={{ minWidth: 60 }}
                          />
                        </Grid>
                        <Grid item xs={4}>
                          <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                            Quantity
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {trade.fill_quantity}
                          </Typography>
                        </Grid>
                        <Grid item xs={4}>
                          <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                            Trade Price
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            ₹{trade.fill_price}
                          </Typography>
                        </Grid>
                        <Grid item xs={4}>
                          <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                            Value
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            ₹{(trade.fill_quantity * trade.fill_price).toFixed(2)}
                          </Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                            Status
                          </Typography>
                          <Chip 
                            label={trade.is_error_trade ? "ERROR" : "SUCCESS"} 
                            color={trade.is_error_trade ? "error" : "success"}
                            size="small"
                            sx={{ minWidth: 80 }}
                          />
                        </Grid>
                      </Grid>
                    </Paper>
                  ))}
                </Box>
              ) : (
                // Desktop view - Table layout
                <Table stickyHeader aria-label="sticky table" size={isMobile ? "small" : "medium"}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Order ID</TableCell>
                      <TableCell>Trade ID</TableCell>
                      <TableCell>Symbol</TableCell>
                      <TableCell>Trade Time</TableCell>
                      <TableCell>Buy/Sell</TableCell>
                      <TableCell>Quantity</TableCell>
                      <TableCell>Trade Price</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {trades.map((trade) => (
                      <TableRow hover role="checkbox" tabIndex={-1} key={trade.trade_id || trade.id}>
                        <TableCell>{trade.order_id}</TableCell>
                        <TableCell>{trade.trade_id || trade.id}</TableCell>
                        <TableCell>{trade.trading_symbol}</TableCell>
                        <TableCell>{formatTimeToIST(trade.exchange_time)}</TableCell>
                        <TableCell>
                          <Chip 
                            label={trade.buy_sell === 0 ? "BUY" : "SELL"} 
                            color={trade.buy_sell === 0 ? "success" : "error"}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{trade.fill_quantity}</TableCell>
                        <TableCell>{trade.fill_price}</TableCell>
                        <TableCell>
                          <Chip 
                            label={trade.is_error_trade ? "ERROR" : "SUCCESS"} 
                            color={trade.is_error_trade ? "error" : "success"}
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </>
          )}
        </TableContainer>
        
        <Divider />
        
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage={isMobile ? "Rows" : "Rows per page"}
        />
      </Paper>
      
      {/* Export Modal */}
      <ExportTradeHistoryModal
        open={openExportModal}
        onClose={handleCloseExportModal}
        defaultExchange={exchange}
        title="Export Trade History"
      />
    </Box>
  );
};

export default TradeHistoryDetails; 